<div class="period-selector9" [class.invalid]="invalid()" [class.readonly]="readOnly()"
  [attr.inert]="readOnly() ? '' : null">

  @if (hasTimelineSwitch()) {
    <div class="mode-switch">
      <button type="button" class="tab" [class.active]="!isScheduleMode()" (click)="setTimelineMode('timeline')">
        <span class="tab-icon timeline"></span>
        <span class="text" i18n>Timeline</span>
      </button>
      <button type="button" class="tab" [class.active]="isScheduleMode()" (click)="setTimelineMode('schedule')">
        <span class="tab-icon schedule"></span>
        <span class="text" i18n>Schedule</span>
      </button>
    </div>
  }

  <div class="group">
    <!-- The clock hints that this selector takes a time of day, not just a date. -->
    @if (hasModePill()) {
      @if (modeOptions().length > 1) {
        <app-select-dropdown class="mode-dropdown"
          [options]="modeSelectOptions()"
          [value]="effectiveMode()"
          [matSymbol]="modeSymbol()"
          (valueChange)="selectMode($event)">
        </app-select-dropdown>
      } @else {
        <div class="pill static">
          <i class="material-symbols-outlined filled">{{ showTime() ? 'calendar_clock' : 'calendar_today' }}</i>
          <span class="text">{{ modeLabel() }}</span>
        </div>
      }
    }

    @if (effectiveMode() === 'day') {
      <div class="pill">
        <button type="button" class="cell step" [disabled]="prevDayDisabled()" (click)="stepDay(-1)"
          i18n-aria-label aria-label="Previous day">
          <i class="material-icons-outlined">chevron_left</i>
        </button>
        @if (dayPrefix()) {
          <span class="cell prefix">{{ dayPrefix() }}</span>
        }
        <span class="cell">
          <span class="field" cdkOverlayOrigin #dayTrigger="cdkOverlayOrigin">
            <input type="text" [attr.inputmode]="dateInputMode()" autocomplete="off" [readonly]="!allowTyping()"
              [style.width.ch]="endDateWidth()"
              [ngModel]="dayField.value()" (ngModelChange)="dayField.type($event)" (blur)="dayField.finish()"
              (keydown.enter)="commitOnEnter($event)"
              (click)="selectAll($event); togglePanel('day')" i18n-aria-label aria-label="Date"/>
          </span>
          <ng-template cdkConnectedOverlay appOverlayPanel
            [cdkConnectedOverlayOrigin]="dayTrigger"
            [cdkConnectedOverlayOpen]="openPanel() === 'day'"
            [panelPositions]="datePickerPositions"
            (panelClose)="openPanel.set(null)">
            <app-date-picker [value]="endDate()" [min]="minDate()" [max]="maxDate()" [timezone]="effectiveTimezone()" (dateSelect)="pickDay($event)"/>
          </ng-template>
        </span>
        <button type="button" class="cell step" [disabled]="nextDayDisabled()" (click)="stepDay(1)"
          i18n-aria-label aria-label="Next day">
          <i class="material-icons-outlined">chevron_right</i>
        </button>
      </div>
    }

    @if (effectiveMode() === 'range') {
      <div class="pill">
        <span class="cell endpoint">
          <span class="field" cdkOverlayOrigin #startTrigger="cdkOverlayOrigin">
            <input type="text" [attr.inputmode]="dateInputMode()" autocomplete="off" [readonly]="!allowTyping()"
              [style.width.ch]="startDateWidth()"
              [ngModel]="startDateField.value()" (ngModelChange)="startDateField.type($event)"
              (blur)="startDateField.finish()"
              (keydown.enter)="commitOnEnter($event)" (click)="selectAll($event); togglePanel('start')"
              i18n-aria-label aria-label="Start date"/>
          </span>
          <ng-template cdkConnectedOverlay appOverlayPanel
            [cdkConnectedOverlayOrigin]="startTrigger"
            [cdkConnectedOverlayOpen]="openPanel() === 'start'"
            [panelPositions]="datePickerPositions"
            (panelClose)="openPanel.set(null)">
            <app-date-picker [value]="startDate()" [min]="minDate()" [max]="maxDate()" [timezone]="effectiveTimezone()" (dateSelect)="pickStartDate($event)"/>
          </ng-template>
          @if (showTime()) {
            <app-time-picker class="time" [class.twelve-hour]="hourFormat() === '12h'" [bare]="true" [clearable]="true"
              [time]="startEndpointTime()" (timeChange)="setStartTime($event)"
              [placeholder]="startTimePlaceholder()" i18n-ariaLabel ariaLabel="Start time"/>
          }
        </span>
        <i class="material-icons-outlined cell arrow">chevron_right</i>
        <span class="cell endpoint">
          <span class="field" cdkOverlayOrigin #endTrigger="cdkOverlayOrigin">
            <input type="text" [attr.inputmode]="dateInputMode()" autocomplete="off" [readonly]="!allowTyping()"
              [style.width.ch]="endDateWidth()"
              [ngModel]="endDateField.value()" (ngModelChange)="endDateField.type($event)"
              (blur)="endDateField.finish()"
              (keydown.enter)="commitOnEnter($event)" (click)="selectAll($event); togglePanel('end')"
              i18n-aria-label aria-label="End date"/>
          </span>
          <ng-template cdkConnectedOverlay appOverlayPanel
            [cdkConnectedOverlayOrigin]="endTrigger"
            [cdkConnectedOverlayOpen]="openPanel() === 'end'"
            [panelPositions]="datePickerPositions"
            (panelClose)="openPanel.set(null)">
            <app-date-picker [value]="endDate()" [min]="minDate()" [max]="maxDate()" [timezone]="effectiveTimezone()" (dateSelect)="pickEndDate($event)"/>
          </ng-template>
          @if (showTime()) {
            <app-time-picker class="time" [class.twelve-hour]="hourFormat() === '12h'" [bare]="true" [clearable]="true"
              [time]="endEndpointTime()" (timeChange)="setEndTime($event)"
              [placeholder]="endTimePlaceholder()" i18n-ariaLabel ariaLabel="End time"/>
          }
        </span>
      </div>
    }

    @if (effectiveMode() === 'recent') {
      <div class="pill static">
        <span class="text" i18n>Last</span>
      </div>
      <div class="pill stepper">
        <button type="button" class="cell step" [disabled]="recentValue() <= effectiveMinRecent()"
          (click)="stepRecent(-1)" i18n-aria-label aria-label="Decrease">
          <i class="material-icons-outlined">remove</i>
        </button>
        <span class="cell">
          <input type="number" #recentInput [min]="effectiveMinRecent()" [max]="maxRecent()"
            [ngModel]="recentValue()" (ngModelChange)="setRecentValue($event)"
            (blur)="syncRecentInput(recentInput)"
            (keydown.enter)="syncRecentInput(recentInput); commitOnEnter($event)"
            (click)="selectAll($event)" i18n-aria-label aria-label="Amount"/>
        </span>
        <button type="button" class="cell step" [disabled]="recentValue() >= maxRecent()" (click)="stepRecent(1)"
          i18n-aria-label aria-label="Increase">
          <i class="material-icons-outlined">add</i>
        </button>
      </div>
      <!-- A datetime selector offers hours only, and one option needs no dropdown. -->
      @if (recentUnitOptions().length > 1) {
        <app-select-dropdown
          [options]="recentUnitSelectOptions()"
          [value]="effectiveRecentUnit()"
          (valueChange)="selectRecentUnit($event)">
        </app-select-dropdown>
      } @else {
        <div class="pill static">
          <span class="text">{{ recentUnitLabel() }}</span>
        </div>
      }
    }

    @if (isScheduleMode()) {
      <button type="button" class="pill weekends" (click)="toggleWeekends()">
        <span class="checkbox" [class.checked]="weekends()">
          <i class="material-icons-outlined">check</i>
        </span>
        <span class="text" i18n>Sat/Sun</span>
      </button>
    }
  </div>

  @if (usesTimeframe()) {
    <div class="group">
      <div class="pill static">
        <i class="material-icons-outlined">schedule</i>
        <span class="text" i18n>Hours</span>
      </div>
      <div class="pill">
        <span class="cell">
          <app-time-picker class="time" [class.twelve-hour]="hourFormat() === '12h'" [bare]="true" [clearable]="true"
            [(time)]="startTime" [placeholder]="startTimePlaceholder()"
            i18n-ariaLabel ariaLabel="Timeframe start"/>
        </span>
        <i class="material-icons-outlined cell arrow">chevron_right</i>
        <span class="cell">
          <app-time-picker class="time" [class.twelve-hour]="hourFormat() === '12h'" [bare]="true" [clearable]="true"
            [(time)]="endTime" [placeholder]="endTimePlaceholder()"
            i18n-ariaLabel ariaLabel="Timeframe end"/>
        </span>
      </div>
    </div>
  }
</div>
